import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TablePagination,
} from '@mui/material';
import { Timeline } from '@mui/icons-material';
import SummaryCards from '../components/SummaryCards';
import { formatDate, getStatusColor, formatStatusValue } from '../constants';

const ActivityLogView = ({ reportData, onPageChange }) => {
    if (!reportData) return null;

    const { events = [], totalEvents = 0, page = 0, pageSize = 100 } = reportData;

    const summaryCards = [
        {
            title: 'Total Events',
            value: totalEvents,
            icon: Timeline,
            color: 'primary',
        },
    ];

    const handleChangePage = (event, newPage) => {
        if (onPageChange) {
            onPageChange(newPage, pageSize);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const newPageSize = parseInt(event.target.value, 10);
        if (onPageChange) {
            onPageChange(0, newPageSize);
        }
    };

    return (
        <Box>
            <SummaryCards cards={summaryCards} />

            <Box sx={{ mt: 4 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Activity Log
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Detailed event history showing who made changes and what changed across all
                        deficiencies. Track user actions for audits and investigations.
                    </Typography>
                </Box>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Modified By
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                        display: { xs: 'none', md: 'table-cell' },
                                    }}
                                >
                                    Change Description
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Deficiency
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.5px',
                                        display: { xs: 'none', sm: 'table-cell' },
                                    }}
                                >
                                    Assigned To
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((event, index) => (
                                <TableRow
                                    key={`${event.deficiencyId}-${event.eventType}-${index}`}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell>{formatDate(event.eventDate)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {event.modifiedByUserName || 'System'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontStyle: 'italic' }}
                                        >
                                            {event.changeDescription || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{ maxWidth: 200, fontWeight: 500 }}
                                        >
                                            {event.deficiencyTitle}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatStatusValue(event.currentStatus)}
                                            size="small"
                                            sx={{
                                                bgcolor: getStatusColor(event.currentStatus),
                                                color: 'white',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                borderRadius: 1,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {event.assignedToCompanyName || 'Unassigned'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalEvents}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                    />
                </TableContainer>
            </Box>
        </Box>
    );
};

export default ActivityLogView;
